import lightThemeTokens from './tokens';
import type { Theme } from './types';

const {
	risk_colors,
	primary_colors,
	secondary_colors,
	border_radius,
	font_sizes,
	font_weights,
	line_heights,
	letter_spacings,
	font_family
} = lightThemeTokens;

export const lightTheme: Theme = {
	title: 'light',
	colors: {
		primary: primary_colors.default,
		danger: risk_colors.high.default,
		success: risk_colors.low.default,
		title: secondary_colors['500'],
		text: secondary_colors['500'],
		risk: {
			low: risk_colors.low.default,
			medium: risk_colors.medium.default,
			moderate: risk_colors.moderate.default,
			high: risk_colors.high.default,
			very_high: risk_colors.very_high.default
		}
	},
	palette: {
		risk: risk_colors,
		primary: primary_colors,
		secondary: secondary_colors
	},
	border: {
		input: {
			radius: border_radius.br_sm
		},
		table: {
			radius: border_radius.br_md
		},
		border_radius
	},
	fonts: {
		display: {
			lg: {
				fontSize: font_sizes.fs_7xl,
				lineHeight: line_heights.lh_8xl,
				letterSpacing: letter_spacings.ls_lg,
				fontWeight: font_weights.semibold
			},
			md: {
				fontSize: font_sizes.fs_6xl,
				lineHeight: line_heights.lh_7xl,
				letterSpacing: letter_spacings.ls_6xl,
				fontWeight: font_weights.semibold
			},
			sm: {
				fontSize: font_sizes.fs_5xl,
				lineHeight: line_heights.lh_6xl,
				letterSpacing: letter_spacings.ls_md,
				fontWeight: font_weights.semibold
			},
			xs: {
				fontSize: font_sizes.fs_3xl,
				lineHeight: line_heights.lh_3xl,
				letterSpacing: letter_spacings.ls_8xl,
				fontWeight: font_weights.semibold
			}
		},
		heading: {
			lg: {
				fontSize: font_sizes.fs_5xl,
				lineHeight: line_heights.lh_5xl,
				letterSpacing: letter_spacings.ls_md,
				fontWeight: font_weights.semibold
			},
			md: {
				fontSize: font_sizes.fs_3xl,
				lineHeight: line_heights.lh_3xl,
				letterSpacing: letter_spacings.ls_xl,
				fontWeight: font_weights.semibold
			},
			sm: {
				fontSize: font_sizes.fs_xl,
				lineHeight: line_heights.lh_3xl,
				letterSpacing: letter_spacings.ls_2xl,
				fontWeight: font_weights.semibold
			},
			xs: {
				fontSize: font_sizes.fs_md,
				lineHeight: line_heights.lh_2xl,
				letterSpacing: letter_spacings.ls_2xs,
				fontWeight: font_weights.semibold
			}
		},
		body: {
			lg: {
				fontSize: font_sizes.fs_lg,
				lineHeight: line_heights.lh_2xl,
				fontWeight: font_weights.regular
			},
			md: {
				fontSize: font_sizes.fs_md,
				lineHeight: line_heights.lh_2xl,
				fontWeight: font_weights.regular
			},
			sm: {
				fontSize: font_sizes.fs_sm,
				lineHeight: line_heights.lh_lg,
				fontWeight: font_weights.regular
			}
		},
		label: {
			lg: {
				fontSize: font_sizes.fs_lg,
				lineHeight: line_heights.lh_2xl,
				fontWeight: font_weights.semibold
			},
			md: {
				fontSize: font_sizes.fs_md,
				lineHeight: line_heights.lh_2xl,
				fontWeight: font_weights.semibold
			},
			sm: {
				fontSize: font_sizes.fs_sm,
				lineHeight: line_heights.lh_sm,
				fontWeight: font_weights.semibold
			}
		},
		label_regular: {
			lg: {
				fontSize: font_sizes.fs_lg,
				lineHeight: line_heights.lh_2xl,
				letterSpacing: letter_spacings.ls_3xl,
				fontWeight: font_weights.regular
			},
			md: {
				fontSize: font_sizes.fs_md,
				lineHeight: line_heights.lh_2xl,
				letterSpacing: letter_spacings.ls_4xl,
				fontWeight: font_weights.regular
			},
			sm: {
				fontSize: font_sizes.fs_sm,
				lineHeight: line_heights.lh_sm,
				letterSpacing: letter_spacings.ls_5xl,
				fontWeight: font_weights.regular
			}
		},
		pdf_display: {
			lg: {
				fontSize: font_sizes.fs_4xl,
				lineHeight: line_heights.lh_4xl,
				letterSpacing: letter_spacings.ls_xs,
				fontWeight: font_weights.semibold
			},
			sm: {
				fontSize: font_sizes.fs_2xl,
				lineHeight: line_heights.lh_4xl,
				letterSpacing: letter_spacings.ls_sm,
				fontWeight: font_weights.semibold
			}
		},
		pdf_heading: {
			lg: {
				fontSize: font_sizes.fs_lg,
				lineHeight: line_heights.lh_2xl,
				fontWeight: font_weights.semibold
			},
			md: {
				fontSize: font_sizes.fs_md,
				lineHeight: line_heights.lh_xl,
				fontWeight: font_weights.semibold
			},
			sm: {
				fontSize: font_sizes.fs_sm,
				lineHeight: line_heights.lh_lg,
				fontWeight: font_weights.semibold
			}
		},
		pdf_body: {
			lg: {
				fontSize: font_sizes.fs_xs,
				lineHeight: line_heights.lh_md,
				fontWeight: font_weights.semibold
			},
			sm: {
				fontSize: font_sizes.fs_2xs,
				lineHeight: line_heights.lh_md,
				fontWeight: font_weights.semibold
			}
		},
		pdf_body_regular: {
			lg: {
				fontSize: font_sizes.fs_xs,
				lineHeight: line_heights.lh_md,
				fontWeight: font_weights.regular
			},
			sm: {
				fontSize: font_sizes.fs_2xs,
				lineHeight: line_heights.lh_md,
				fontWeight: font_weights.regular
			}
		},
		pdf_label: {
			xs: {
				fontSize: font_sizes.fs_3xs,
				lineHeight: line_heights.lh_xs,
				fontWeight: font_weights.semibold
			}
		},
		pdf_label_regular: {
			xs: {
				fontSize: font_sizes.fs_3xs,
				lineHeight: line_heights.lh_xs,
				fontWeight: font_weights.regular
			}
		}
	},
	sizes: {
		fonts: font_sizes,
		weights: font_weights,
		line_heights,
		letter_spacings
	},
	font_family: {
		primary: font_family.primary
	}
};
